import type { ClaimantType, PaginatedResponse } from "types/DataTypes.ts";

import { api } from "./api.ts";

type GetClaimantsArgs = {
  searchTerm?: string;
  limit?: number;
  offset?: number;
};

type GetClaimantByIdArgs = {
  id: string | number;
};

type PatchClaimantArgs = {
  id: string | number;
  data: Partial<ClaimantType>;
};

type CreateClaimantArgs = {
  data: Partial<ClaimantType>;
};

type DeleteClaimantArgs = {
  id: string | number;
};

export const claimantAPI = api.injectEndpoints({
  endpoints: (build) => ({
    getClaimants: build.query<
      PaginatedResponse<ClaimantType>,
      GetClaimantsArgs
    >({
      query: ({ searchTerm, limit, offset }) => ({
        url: `api/v1/claimants/`,
        method: "GET",
        params: {
          ...(searchTerm ? { filter: searchTerm } : {}),
          ...(limit !== undefined ? { limit } : {}),
          ...(offset !== undefined ? { offset } : {}),
        },
      }),
      providesTags: ["Claimants"],
    }),
    getClaimantById: build.query<ClaimantType, GetClaimantByIdArgs>({
      query: ({ id }) => ({
        url: `api/v1/claimants/${id}/`,
        method: "GET",
      }),
    }),
    patchClaimantById: build.mutation<ClaimantType, PatchClaimantArgs>({
      query: ({ id, data }) => ({
        url: `api/v1/claimants/${id}/`,
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: [
        "Claimants",
        "Files",
        "FileValidation",
        "SuggestedClaimLinks",
        "MSA",
        "MSAValidation",
        "MCP",
        "LCP",
        "CostProjectionValidation",
        "AmendedReviewScreening",
        "AmendedReviewScreeningValidation",
        "DocumentManagementFileValidation",
      ],
    }),
    createClaimant: build.mutation<ClaimantType, CreateClaimantArgs>({
      query: ({ data }) => ({
        url: `api/v1/claimants/`,
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["Claimants", "Files"],
    }),
    deleteClaimant: build.mutation<void, DeleteClaimantArgs>({
      query: ({ id }) => ({
        url: `api/v1/claimants/${id}/`,
        method: "DELETE",
      }),
      invalidatesTags: ["Claimants", "Files"],
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetClaimantsQuery,
  useGetClaimantByIdQuery,
  usePatchClaimantByIdMutation,
  useCreateClaimantMutation,
  useDeleteClaimantMutation,
} = claimantAPI;
